/* eslint-disable max-len */
import { useEffect } from 'react';
import proptypes from 'prop-types';
import {
  arrayOf as arrayType,
  bool as boolType,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  json,
  useDataModel
} from '@thd-nucleus/data-sources';
import { useStoreId } from '@thd-nucleus/experience-context';
import {
  useHelmet, TitleTag, MetaTag, ScriptTag, LinkTag
} from '@thd-nucleus/thd-helmet';
import { copy } from '@thd-olt-functional/utils';
import {
  formatProductLabel,
  getDynamicOGTags,
  getHoistedImgPath,
  getPrimaryImage,
  getSecondaryImgPath
} from '../../helpers/product-helpers';
import { filterEmtMetadataOverrides } from '../../helpers/emt-content-helpers';
import {
  getVideoStructuredData,
  getBreadcrumbStructuredData,
  getProductStructuredData,
  getProductGroupSchema
} from '../structured-data';
import {
  getTitle,
  getBrandName,
  getDescription,
  getRobotsTag,
  getAggregateReviews
} from "./product-metadata-helpers.js";

const { bool, string } = proptypes;

const productMetaDataHandler = ({ data, props = {} }) => {
  if (!data?.product) {
    return [];
  }

  const { productGroup } = data?.emtContent?.content || {};
  const {
    itemId,
    details,
    identifiers,
    seo,
    availabilityType,
    media,
    taxonomy,
    pricing,
    specificationGroup,
    info
  } = data?.product || {};

  const productLabel = formatProductLabel({ identifiers });
  const returnable = info?.returnable;
  const brandName = getBrandName(identifiers);
  const title = getTitle(brandName, identifiers, props);
  const description = getDescription(seo, details, props);
  const { canonicalUrl: canonicalPath = '' } = identifiers || {};

  const canonical = `https://www.homedepot.com${canonicalPath}`;
  const { video } = media || {};
  const primaryImage = getPrimaryImage({ media });
  const hoistedImgPath = getHoistedImgPath({ primaryImage });
  const secondaryImgPath = getSecondaryImgPath({ media });
  const alternate = `android-app://com.thehomedepot/homedepot/product/${itemId}`;

  const emtContentMetadata = data?.emtContent?.content?.metadata || {};
  const {
    metaDescription: emtMetaDescription = description,
    ogDescription: emtOgDescription = description,
    ogImage: emtOgImage = primaryImage,
    ogTitle: emtOgTitle = title,
    ogType: emtOgType,
    schema: emtSchema,
    title: emtTitle = title,
  } = filterEmtMetadataOverrides(emtContentMetadata);

  const dynamicOGTags = getDynamicOGTags({
    props, brandName, identifiers, title: emtOgTitle, description: emtOgDescription, type: emtOgType
  });

  const tags = [
    new TitleTag(emtTitle),
    new MetaTag({ name: 'description', content: emtMetaDescription, id: 'description' }),
    ...dynamicOGTags,
    new MetaTag({ property: 'og:url', content: canonical, id: 'ogUrl' }),
    new MetaTag({ property: 'og:image', content: emtOgImage, id: 'ogImage' }),
    new MetaTag({ property: 'og:site_name', content: 'The Home Depot', id: 'ogSiteName' }),
    new MetaTag({ property: 'fb:admins', content: '100005416960152', id: 'fbAdmins' }),
    new LinkTag({ rel: 'canonical', href: canonical, id: 'canonical' }),
    new LinkTag({ rel: 'alternate', href: alternate, id: 'alternate' }),
    new LinkTag({
      rel: 'preload', href: hoistedImgPath, id: 'preloadImg', as: 'image', fetchpriority: 'high'
    }),
  ];
  // TODO: Verify if we still need this since we switched to a carousel years ago, which no longer shows the secondary image on page load
  if (secondaryImgPath) {
    tags.push(new LinkTag({
      rel: 'preload', href: secondaryImgPath, id: 'preloadSecondaryImg', as: 'image', fetchpriority: 'high'
    }));
  }

  if (emtSchema) {
    tags.push(new ScriptTag({ content: emtSchema, id: 'productStructureData' }));
  } else {
    const aggregateReviews = getAggregateReviews(data);

    const productStructure = getProductStructuredData({
      aggregateReviews: !(props?.hideIfHDPPSku && identifiers?.skuClassification === 'hdpp') && aggregateReviews,
      reviews: !(props?.hideIfHDPPSku && identifiers?.skuClassification === 'hdpp') && data?.reviews?.Results,
      identifiers: {
        ...identifiers,
        productLabel
      },
      media,
      details,
      pricing,
      specifications: specificationGroup,
      availabilityType,
      schemaExclude: props.schema,
      ppq: props.ppq,
      returnable,
    });

    tags.push(new ScriptTag({ content: productStructure, id: 'productStructureData' }));

    const breadcrumbStructuredData = getBreadcrumbStructuredData({
      canonical,
      breadcrumbs: copy(taxonomy?.breadCrumbs || []).data,
      title,
      brandName,
      brandLinkUrl: taxonomy?.brandLinkUrl
    });
    tags.push(new ScriptTag({ content: breadcrumbStructuredData, id: 'breadcrumbStructureData' }));

    const robotsTag = getRobotsTag(availabilityType);
    tags.push(robotsTag);

    if (video?.length) {
      const videoStructuredData = getVideoStructuredData({
        videos: video,
        productLabel,
      });
      tags.push(new ScriptTag({ content: videoStructuredData, id: 'videoStructureData' }));
    }

    if (productGroup?.productGroupName) {
      const productGroupSchema = getProductGroupSchema({
        product: data.product,
        reviews: data.reviews,
        groupName: productGroup.productGroupName
      });

      tags.push(new ScriptTag({ content: productGroupSchema, id: 'productGroup' }));
    }
  }

  return tags;
};

const ProductMetadata = ({
  itemId,
  pageDescription = null,
  pageNumber = null,
  pageTitle = null,
  ppq = false,
  questionsCanonical = false,
  reviewsCanonical,
  hideIfHDPPSku
}) => {
  const pageSize = 30;
  const storeId = useStoreId();
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-metadata.ready'); }, []);
  const productOptions = {
    variables: {
      itemId,
      storeId,
    },
  };

  let startIndex = (pageNumber - 1) * pageSize;
  startIndex = pageNumber > 1 ? startIndex + 1 : startIndex;

  const reviewsOptions = {
    variables: {
      itemId,
      ...(pageNumber && {
        startIndex,
        pagesize: pageSize,
        recfirstpage: pageSize
      })
    },
  };

  const emtContentOptions = {
    variables: {
      contentId: itemId,
      pageName: 'pip'
    },
    skip: !itemId
  };

  const { data, loading, error, variables } = useDataModel('product', productOptions);
  const { data: reviewData, loading: reviewLoading, reviewError } = useDataModel('reviews', reviewsOptions);
  const { data: emtData, loading: emtLoading, error: emtError } = useDataModel('emtContent', emtContentOptions);

  const props = {
    reviewsCanonical,
    pageDescription,
    pageNumber,
    pageTitle,
    questionsCanonical,
    ppq,
    hideIfHDPPSku
  };

  const helmetData = {
    product: data?.product,
    reviews: !(props?.hideIfHDPPSku && data?.product?.identifiers?.skuClassification === 'hdpp') && reviewData?.reviews,
    emtContent: emtData?.emtContent,
  };

  const deps = data?.product && reviewData?.reviews && data?.product?.itemId;

  useHelmet('productMetadata', { data: helmetData, props, variables, skipCleanup: true }, productMetaDataHandler, [deps]);
  return null;
};

ProductMetadata.displayName = 'ProductMetadata';

ProductMetadata.dataModel = {
  product: params({ itemId: stringType().isRequired() }).shape({
    itemId: stringType(),
    dataSources: stringType(),
    specificationGroup: arrayType(shapeType({
      specifications: arrayType(shapeType({
        specName: stringType(),
        specValue: stringType()
      })),
      specTitle: stringType()
    })),
    availabilityType: shapeType({
      discontinued: boolType(),
      status: boolType()
    }),
    details: shapeType({
      description: stringType()
    }),
    identifiers: shapeType({
      brandName: stringType(),
      canonicalUrl: stringType(),
      itemId: stringType(),
      modelNumber: stringType(),
      productLabel: stringType(),
      storeSkuNumber: stringType(),
      upcGtin13: stringType(),
      skuClassification: stringType()
    }),
    media: shapeType({
      image: shapeType({
        url: stringType()
      }).client(),
      images: arrayType(shapeType({
        url: stringType(),
        type: stringType(),
        subType: stringType(),
        sizes: arrayType(stringType())
      })),
      video: arrayType(shapeType({
        shortDescription: stringType(),
        thumbnail: stringType(),
        url: stringType(),
        videoStill: stringType(),
        uploadDate: stringType(),
        dateModified: stringType(),
      }))
    }),
    pricing: params({ storeId: stringType() }).shape({
      promotion: shapeType({
        dates: shapeType({
          end: stringType()
        })
      }),
      value: numberType({ float: true }),
      original: numberType({ float: true })
    }),
    reviews: shapeType({
      ratingsReviews: shapeType({
        averageRating: stringType(),
        totalReviews: stringType()
      })
    }),
    seo: shapeType({
      seoKeywords: stringType(),
      seoDescription: stringType()
    }),
    taxonomy: shapeType({
      breadCrumbs: arrayType(shapeType({
        label: stringType(),
        url: stringType()
      }))
    })
  }),
  reviews: params({ itemId: stringType().isRequired() }).shape({
    Results: arrayType(shapeType({
      Rating: numberType(),
      ReviewText: stringType(),
      Title: stringType(),
      UserNickname: stringType()
    }))
  }),
  emtContent: params({
    contentId: stringType().isRequired(),
    pageName: stringType().isRequired()
  }).shape({
    content: json()
  })
};

ProductMetadata.propTypes = {
  itemId: string.isRequired,
  reviewsCanonical: bool,
  pageNumber: string,
  pageTitle: string,
  pageDescription: string,
  questionsCanonical: bool,
  ppq: bool,
  hideIfHDPPSku: bool
};

ProductMetadata.defaultProps = {
  reviewsCanonical: false,
  pageNumber: null,
  pageTitle: null,
  pageDescription: null,
  questionsCanonical: false,
  ppq: false,
  hideIfHDPPSku: false
};

export { ProductMetadata };
